import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload as UploadIcon, FileSpreadsheet, FileText, Check, AlertCircle, AlertTriangle } from "lucide-react";
import { ddmmmyy } from "@/lib/format";

const STEPS = ["Uploading", "Parsing invoice", "Parsing entry summary", "Calculating", "Done"];

const Dropzone = ({ id, label, accept, file, onPick, icon: Icon, testId }) => {
  const ref = useRef();
  const [hover, setHover] = useState(false);
  return (
    <div
      data-testid={testId}
      onClick={() => ref.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setHover(true); }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHover(false);
        if (e.dataTransfer.files?.[0]) onPick(e.dataTransfer.files[0]);
      }}
      className="rounded-lg p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-colors"
      style={{
        border: "2px dashed " + (hover || file ? "#0F2044" : "#E5E7EB"),
        background: file ? "#F8F9FC" : "#FFFFFF",
        minHeight: 200,
      }}
    >
      <input
        ref={ref} type="file" accept={accept}
        data-testid={`${testId}-input`}
        className="hidden"
        onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])}
      />
      <Icon size={36} className="text-[#0F2044] mb-2" />
      <div className="text-sm font-semibold text-[#0F2044] mb-1">{label}</div>
      {file ? (
        <>
          <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-semibold">
            <Check size={14} /> {file.name}
          </div>
          <div className="text-xs text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} KB</div>
        </>
      ) : (
        <div className="text-xs text-gray-400">Click or drag file here · {accept}</div>
      )}
    </div>
  );
};

const Upload = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [incoterm, setIncoterm] = useState("DDP");
  const [invoice, setInvoice] = useState(null);
  const [entry, setEntry] = useState(null);
  const [step, setStep] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [master, setMaster] = useState(null);
  const [duplicate, setDuplicate] = useState(null); // 409 conflict payload

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    apiClient.get("/parts-master").then(({ data }) => setMaster(data)).catch(() => {});
  }, []);

  const masterReady = master?.configured && master?.count > 0;

  const submit = async () => {
    if (!masterReady) {
      toast.error("Parts-Tariff Master is not configured. Ask an admin to upload it from Settings.");
      return;
    }
    if (!invoice || !entry) {
      toast.error("Please upload both documents");
      return;
    }
    setBusy(true);
    setStep(0);
    const fd = new FormData();
    fd.append("incoterm", incoterm);
    fd.append("invoice_file", invoice);
    fd.append("entry_file", entry);

    let active = true;
    const advance = async () => {
      for (let i = 1; i < STEPS.length - 1; i++) {
        if (!active) return;
        await new Promise((r) => setTimeout(r, 1200));
        setStep(i);
      }
    };
    advance();

    try {
      const { data } = await apiClient.post("/shipments/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      active = false;
      setStep(STEPS.length - 1);
      toast.success(`Reconciled ${data.reference}`);
      setTimeout(() => navigate(`/shipments/${data.reference}`), 500);
    } catch (err) {
      active = false;
      setStep(-1);
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "DUPLICATE_SHIPMENT") {
        setDuplicate(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : detail?.message || "Upload failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">New shipment</div>
        <h1 className="text-2xl font-bold text-[#0F2044]">Upload reconciliation documents</h1>
        <p className="text-sm text-gray-600 mt-1">
          Provide the Commercial Invoice and the CBP Entry Summary. Duty per part comes from the Parts-Tariff Master.
        </p>
      </div>

      {!masterReady && master && (
        <div
          data-testid="master-warning"
          className="flex items-start gap-3 p-4 bg-white rounded-md shadow-sm mb-6"
          style={{ border: "1px solid var(--color-border)", borderLeftWidth: 4, borderLeftColor: "#F59E0B" }}
        >
          <AlertCircle className="text-amber-500 mt-0.5" size={20} />
          <div className="text-sm">
            <div className="font-semibold text-[#0F2044]">Parts-Tariff Master is not configured.</div>
            <div className="text-gray-600">An admin must upload it from <span className="font-mono">Settings → Parts-Tariff Master</span> before any reconciliation can run.</div>
          </div>
        </div>
      )}

      {masterReady && (
        <div className="bg-white rounded-md p-3 mb-6 text-xs flex items-center justify-between" style={{ border: "1px solid var(--color-border)" }} data-testid="master-banner">
          <div>
            <span className="text-gray-500 uppercase tracking-wider">Parts-Tariff Master · </span>
            <span className="font-semibold text-[#0F2044]">{master.count} parts</span>
            <span className="text-gray-400 ml-2">· {master.original_filename}</span>
          </div>
          <div className="text-gray-400">Uploaded by {master.uploaded_by}</div>
        </div>
      )}

      {step >= 0 && (
        <div className="bg-white p-4 rounded-md mb-6 flex flex-wrap gap-3" style={{ border: "1px solid var(--color-border)" }} data-testid="upload-progress">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 text-xs">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center font-semibold"
                style={{
                  background: i <= step ? "#0F2044" : "#E5E7EB",
                  color: i <= step ? "white" : "#6B7280",
                }}
              >
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <span className={i <= step ? "text-[#0F2044] font-semibold" : "text-gray-400"}>{s}</span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white p-6 rounded-md mb-6" style={{ border: "1px solid var(--color-border)" }}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Session reference
            </label>
            <div className="px-3 py-2 rounded-md text-sm font-mono text-gray-700" style={{ border: "1px solid var(--color-border)", background: "#F8F9FC" }} data-testid="session-ref">
              Auto-generated on submit
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Incoterm
            </label>
            <select
              data-testid="incoterm-select"
              value={incoterm}
              onChange={(e) => setIncoterm(e.target.value)}
              className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2044]/30"
              style={{ border: "1px solid var(--color-border)", background: "white" }}
            >
              {["DDP", "FOB", "CIF", "CFR", "EXW", "Other"].map((i) => (
                <option key={i}>{i}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Dropzone
            testId="dz-invoice"
            label="Commercial Invoice (Excel)"
            accept=".xlsx,.xls"
            file={invoice}
            onPick={setInvoice}
            icon={FileSpreadsheet}
          />
          <Dropzone
            testId="dz-entry"
            label="CBP Entry Summary (PDF)"
            accept=".pdf"
            file={entry}
            onPick={setEntry}
            icon={FileText}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          data-testid="upload-submit"
          onClick={submit}
          disabled={busy || !masterReady}
          className="bg-[#0F2044] text-white px-6 py-2.5 rounded-md text-sm font-semibold hover:bg-[#1a2f5c] transition-colors disabled:opacity-60 flex items-center gap-2"
        >
          <UploadIcon size={16} />
          {busy ? "Processing..." : "Reconcile shipment"}
        </button>
      </div>

      {/* Duplicate-shipment modal */}
      {duplicate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(15, 32, 68, 0.55)" }}
          data-testid="duplicate-modal"
          onClick={() => setDuplicate(null)}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={22} />
              <div>
                <h3 className="text-lg font-bold text-[#0F2044]">Duplicate shipment</h3>
                <p className="text-sm text-gray-600 mt-1">
                  A shipment with the same {duplicate.matched_on?.map((f) => f.replace("_", " ")).join(" and ")} already exists.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-md p-3" style={{ background: "#F8F9FC", border: "1px solid var(--color-border)" }}>
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Already in system</div>
                <div className="text-sm space-y-1">
                  <div><span className="text-gray-500">Reference </span><span className="font-mono">{duplicate.existing?.reference}</span></div>
                  <div><span className="text-gray-500">Invoice </span><span className="font-mono">{duplicate.existing?.invoice_number || "—"}</span></div>
                  <div><span className="text-gray-500">Entry </span><span className="font-mono">{duplicate.existing?.entry_number || "—"}</span></div>
                  <div><span className="text-gray-500">Customer </span>{duplicate.existing?.customer_name || "—"}</div>
                  <div><span className="text-gray-500">Uploaded </span><span className="uppercase-date">{ddmmmyy(duplicate.existing?.uploaded_at)}</span> · <span className="font-mono">{duplicate.existing?.uploaded_by}</span></div>
                </div>
              </div>
              <div className="rounded-md p-3" style={{ background: "#FEF3C7", border: "1px solid #FCD34D" }}>
                <div className="text-xs uppercase tracking-wider text-amber-800 mb-2">Your new upload</div>
                <div className="text-sm space-y-1 text-amber-900">
                  <div><span className="text-amber-700">Invoice </span><span className="font-mono">{duplicate.incoming?.invoice_number || "—"}</span></div>
                  <div><span className="text-amber-700">Entry </span><span className="font-mono">{duplicate.incoming?.entry_number || "—"}</span></div>
                </div>
              </div>
            </div>

            <div className="rounded-md p-3 mb-5" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
              <p className="text-sm text-blue-900">
                {isAdmin
                  ? "To re-upload, open the existing shipment and delete it first, then try uploading again. Nothing has been saved from this attempt."
                  : "To re-upload, contact an administrator to delete the existing record first. Nothing has been saved from this attempt."}
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <button
                data-testid="dup-close"
                onClick={() => setDuplicate(null)}
                className="px-4 py-2 rounded-md text-sm bg-white hover:bg-slate-50"
                style={{ border: "1px solid var(--color-border)" }}
              >
                Close
              </button>
              <button
                data-testid="dup-open-existing"
                onClick={() => {
                  const ref = duplicate.existing?.reference;
                  setDuplicate(null);
                  if (ref) navigate(`/shipments/${ref}`);
                }}
                className="px-4 py-2 rounded-md text-sm font-semibold bg-[#0F2044] text-white hover:bg-[#1a2f5c]"
              >
                Open existing shipment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Upload;
